import { useLayoutEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

// Screenshots of the demo restaurant (رستوران نارنج) in each menu theme; see
// marketing/menu-screenshots/. Japanese Minimal and Brew Bites are left out:
// they replace the restaurant logo with their own emblem.
const THEMES = [
  { key: "persian-heritage", label: "میراث ایرانی" },
  { key: "mizban-signature", label: "امضای میزبان" },
  { key: "royal-gold", label: "رویال" },
  { key: "classic", label: "کلاسیک" },
  { key: "luxury-white", label: "لوکس سفید" },
  { key: "garden-fresh", label: "گاردن فرش" },
  { key: "nova", label: "نوا" },
  { key: "street-food", label: "فست‌فود خیابانی" },
];

const toPersianDigits = (value) =>
  String(value).replace(/\d/g, (digit) => "۰۱۲۳۴۵۶۷۸۹"[Number(digit)]);

export default function ThemesShowcase() {
  const sectionRef = useRef(null);

  // Cinematic catalogue: the headline rises out of a mask and each phone is
  // uncovered by a curtain wipe. On desktop the section then pins and
  // vertical scroll slides the phone strip sideways like a film reel, each
  // phone growing as it reaches the centre. GSAP only animates [data-*]
  // wrappers: this project's Tailwind classes are !important and would
  // override its inline transforms.
  useLayoutEffect(() => {
    const root = sectionRef.current;
    if (!root) return undefined;

    const media = gsap.matchMedia();
    const phones = () => root.querySelectorAll("[data-theme-phone]");

    media.add("(prefers-reduced-motion: no-preference)", () => {
      gsap
        .timeline({
          scrollTrigger: {
            trigger: root,
            start: "top 70%",
            toggleActions: "play none none none",
          },
        })
        .fromTo(
          root.querySelectorAll("[data-theme-line]"),
          { yPercent: 110 },
          { yPercent: 0, duration: 1, stagger: 0.12, ease: "power4.out" }
        )
        .fromTo(
          phones(),
          { clipPath: "inset(100% 0% 0% 0% round 36px)" },
          {
            clipPath: "inset(0% 0% 0% 0% round 36px)",
            duration: 1.1,
            stagger: 0.08,
            ease: "power4.inOut",
          },
          "-=0.6"
        );
    });

    media.add(
      "(min-width: 1024px) and (prefers-reduced-motion: no-preference)",
      () => {
        const viewport = root.querySelector("[data-theme-viewport]");
        const track = root.querySelector("[data-theme-track]");
        // RTL: the strip overflows to the left, so moving it right (+x)
        // brings the later themes into view.
        const distance = () => Math.max(0, track.scrollWidth - viewport.clientWidth);

        const reel = gsap
          .timeline({
            defaults: { ease: "none" },
            scrollTrigger: {
              trigger: root,
              start: "top top",
              end: () => `+=${distance()}`,
              pin: true,
              scrub: 1,
              anticipatePin: 1,
              invalidateOnRefresh: true,
            },
          })
          .to(track, { x: () => distance() }, 0)
          .fromTo(
            root.querySelector("[data-theme-progress]"),
            { scaleX: 0 },
            { scaleX: 1, transformOrigin: "right center" },
            0
          );

        phones().forEach((phone) => {
          gsap.fromTo(
            phone,
            { scale: 0.86, autoAlpha: 0.55 },
            {
              scale: 1,
              autoAlpha: 1,
              ease: "none",
              scrollTrigger: {
                trigger: phone,
                containerAnimation: reel,
                start: "right left",
                end: "center center",
                scrub: true,
              },
            }
          );
        });
      }
    );

    return () => media.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      aria-labelledby="themes-title"
      dir="rtl"
      // Position is inline, not Tailwind's `relative`: Tailwind classes are
      // !important here and would override the `position: fixed` that
      // ScrollTrigger applies while pinning (see ProductStory).
      style={{ position: "relative" }}
      className="overflow-hidden bg-[#0d1512] py-16 text-white lg:flex lg:min-h-screen lg:flex-col lg:justify-center"
    >
      <div className="mx-auto mb-10 max-w-3xl px-4 text-center">
        <p className="mb-4 flex items-center justify-center gap-3 text-sm font-bold text-primary">
          <span className="h-px w-10 bg-primary/60" aria-hidden="true" />
          قالب‌های منو
          <span className="h-px w-10 bg-primary/60" aria-hidden="true" />
        </p>
        <h2 id="themes-title" className="text-3xl font-black leading-[1.35] md:text-5xl">
          <span className="block overflow-hidden pb-1">
            <span data-theme-line className="block">منوی شما،</span>
          </span>
          <span className="block overflow-hidden pb-2">
            <span data-theme-line className="block text-primary">به سبک رستوران شما</span>
          </span>
        </h2>
        <p className="mt-4 text-white/70">قالب را با یک کلیک عوض کنید؛ منو همان می‌ماند.</p>
      </div>

      <div className="relative">
        {/* Brand green stage band the phones stand across. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-4 bottom-[26%] top-[18%] rounded-[2.5rem] bg-secondary lg:inset-x-12"
        />
        <div
          data-theme-viewport
          className="relative overflow-x-auto pb-4 [scrollbar-width:none] snap-x snap-mandatory lg:motion-safe:overflow-hidden lg:motion-safe:snap-none"
        >
          <ul
            data-theme-track
            className="flex w-max gap-6 px-[calc(50vw-7.5rem)] lg:gap-10 lg:px-[calc(50vw-7rem)]"
          >
            {THEMES.map(({ key, label }, index) => (
              <li key={key} className="shrink-0 snap-center">
                <div data-theme-phone>
                  <div
                    tabIndex={0}
                    className="group relative aspect-[390/844] w-[15rem] overflow-hidden rounded-[36px] border-[6px] border-[#1f2a26] bg-black shadow-[0_30px_60px_rgba(0,0,0,.5)] outline-none focus-visible:ring-4 focus-visible:ring-primary/50 lg:w-[14rem]"
                  >
                    <img
                      src={`/images/themes/${key}-menu.jpg`}
                      alt={`منوی رستوران نارنج با قالب ${label}`}
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                    {/* Hover (or keyboard focus) reveals the same theme's
                        food detail page. */}
                    <img
                      src={`/images/themes/${key}-detail.jpg`}
                      alt=""
                      aria-hidden="true"
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-700 group-hover:opacity-100 group-focus-visible:opacity-100"
                    />
                  </div>
                </div>
                <p className="mt-5 flex items-center justify-center gap-2 text-center">
                  <span className="text-xs font-bold text-primary">
                    {toPersianDigits(String(index + 1).padStart(2, "0"))}
                  </span>
                  <span className="font-bold">{label}</span>
                </p>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mx-auto mt-8 hidden w-full max-w-md px-4 lg:motion-safe:block" aria-hidden="true">
        <span className="block h-0.5 overflow-hidden rounded-full bg-white/10">
          <span data-theme-progress className="block h-full w-full bg-primary" />
        </span>
      </div>
    </section>
  );
}
